import { removeToken } from '@shared/util/cookie'
import type { ApiCacheOptions } from '@shared/util/request'
import type { Nullable } from '@shared/util/types'
import { defineStore } from 'pinia'
import { clearRequestCache } from '@/api/index'
import type { UserAccountInfo, UserModuleInfo } from '@/api/user'
import { getUserInfo, getUserModuleInfo } from '@/api/user'
import { USER_ROLE } from '@/constants'
import { getModule, isModuleActionAllowed } from '@/utils/module'
import { useCache } from '@/composables/useCache'

interface IUserState {
  userInfo: Nullable<UserAccountInfo> | null
  studentModuleInfo: Nullable<UserModuleInfo> | null
  teacherModuleInfo: Nullable<UserModuleInfo> | null
}
export const useUserStore = defineStore({
  id: 'user',
  state: (): IUserState => ({
    userInfo: null,
    studentModuleInfo: null,
    teacherModuleInfo: null,
  }),
  getters: {
    userSchools: (state) => {
      return state.userInfo?.userSchools || []
    },

    curSchool: (state) => {
      const schoolId = useCache('schoolId').value
      return state.userInfo?.userSchools?.find(item => item.id === Number(schoolId))
      || state.userInfo?.userSchools?.[0]
    },

    teacherRole: state => state.teacherModuleInfo?.role,
    hasTeacherModule() {
      return (moduleId: number) => {
        const modules = this.teacherModuleInfo?.userModuleTreeNodeUIDataList || []
        return !!getModule(modules, moduleId)
      }
    },
    hasTeacherModuleAllowed() {
      return (data: { moduleId: number; action?: string }) => {
        return isModuleActionAllowed(
          this.teacherModuleInfo?.userModuleTreeNodeUIDataList || [],
          data.moduleId,
          data.action,
        )
      }
    },
  },
  actions: {
    getUserInfo(force = false) {
      return getUserInfo({ force }).then((result) => {
        this.userInfo = result.data || null
        return result
      })
    },

    changeSchool(schoolId: number) {
      useCache('schoolId').value = schoolId
    },

    getUserModuleInfo(role: USER_ROLE, cacheOptions?: ApiCacheOptions) {
      return getUserModuleInfo(role, cacheOptions).then((res) => {
        if (role === USER_ROLE.teacher) {
          this.teacherModuleInfo = res.data || null
        } else {
          this.studentModuleInfo = res.data || null
        }
        return res
      })
    },
    logout(cleanAll = true) {
      removeToken()
      cleanAll ? this.clear() : this.shadowClear()
    },
    clear() {
      sessionStorage.clear()
      this.shadowClear()
    },
    shadowClear() {
      this.teacherModuleInfo = null
      this.studentModuleInfo = null
      clearRequestCache()
    },
  },
})
